import { useQuery } from '@tanstack/react-query'
import type {
  SheetInfo,
  SheetStatus,
  StageDisciplineInfo,
  StageStatusOption,
} from '@concours/shared'
import { useAuth } from '@concours/ui'

/** Affectations et saisie des notes (phase 14). */
export const GRADING_KEY = ['admin', 'grading'] as const

export interface MySheet {
  id: string
  status: SheetStatus
  statusLabel: string
  round: number
  commission: string | null
  competition: string
  stage: string
  stageOpen: boolean
  discipline: string
  lot: string
  submittedAt: string | null
  progress: { done: number; total: number }
}

export interface GradingStage {
  id: string
  label: string
  position: number
  status: string
  statusLabel: string
  competition: { id: string; reference: string; title: string }
  sheets: { total: number; submitted: number; validated: number }
}

export interface OverviewSheet {
  id: string
  lotId: string
  stageDisciplineId: string | null
  round: number
  commission: string | null
  evaluator: { id: string; name: string }
  status: SheetStatus
  statusLabel: string
  progress: { done: number; total: number }
  submittedAt: string | null
  validatedAt: string | null
  validatedBy: string | null
}

export interface GradingOverview {
  stage: {
    id: string
    label: string
    position: number
    status: string
    statusLabel: string
    graded: boolean
    population: 'admissible' | 'previous'
    anonymous: boolean
    anonymityLiftedAt: string | null
    statuses: StageStatusOption[]
  }
  competition: { id: string; reference: string; title: string }
  disciplines: StageDisciplineInfo[]
  population: { total: number; unassigned: number }
  candidates: {
    id: string
    lotId: string | null
    applicationNumber: string
    anonymityCode: string | null
    name: string
  }[]
  lots: {
    id: string
    label: string
    room: string | null
    center: string | null
    position: number
    count: number
  }[]
  sheets: OverviewSheet[]
  anomalies: { kind: string; message: string; lotId?: string; stageDisciplineId?: string }[]
}

export interface HistoryEntry {
  id: number
  candidate: string
  before: string
  after: string
  author: string
  reason: string | null
  at: string | null
}

export function useMySheets() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...GRADING_KEY, 'mine'],
    queryFn: () => session.api.get<{ data: MySheet[] }>('/admin/my/sheets').then((r) => r.data),
  })
}

export function useSheet(id: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...GRADING_KEY, 'sheet', id],
    queryFn: () => session.api.get<SheetInfo>(`/admin/sheets/${id}`),
  })
}

export function useSheetHistory(id: string, enabled: boolean) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...GRADING_KEY, 'sheet', id, 'history'],
    queryFn: () =>
      session.api.get<{ data: HistoryEntry[] }>(`/admin/sheets/${id}/history`).then((r) => r.data),
    enabled,
  })
}

export function useGradingStages() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...GRADING_KEY, 'stages'],
    queryFn: () =>
      session.api.get<{ data: GradingStage[] }>('/admin/grading/stages').then((r) => r.data),
  })
}

export function useGradingOverview(stageId: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...GRADING_KEY, 'overview', stageId],
    queryFn: () => session.api.get<GradingOverview>(`/admin/stages/${stageId}/grading`),
  })
}

export function useEvaluators(stageId: string, enabled: boolean) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...GRADING_KEY, 'evaluators', stageId],
    queryFn: () =>
      session.api
        .get<{ data: { id: string; name: string; email: string }[] }>(
          `/admin/stages/${stageId}/evaluators`
        )
        .then((r) => r.data),
    enabled,
    staleTime: 60_000,
  })
}

export const SHEET_VARIANT = {
  draft: 'neutral',
  submitted: 'info',
  validated: 'success',
  unlocked: 'warning',
} as const

/** « 12,5 » pour l'affichage. */
export const fmtScore = (n: number | null | undefined) =>
  n === null || n === undefined ? '' : String(n).replace('.', ',')
